import {identity, noop} from 'lodash';
import {LRUCache} from 'lru-cache';
import React, {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {BehaviorSubject, debounceTime, groupBy, mergeMap, Subject} from 'rxjs';

import {useConstant, useDeepMemo} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {enforceType} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  InboundOptimisticMessage,
  RInboundOptimisticMessage,
} from '../../../../../libs/shared/core/src/models/api/chatDirectPushMessageModel';
import {PushConnectorsEnum} from '../../../../../libs/shared/core/src/models/api/pushConfigModel';
import {buildAblyChannelName} from '../../../../../libs/shared/core/src/services/push/ably/ablyChannelNameHelpers';
import {AblyController} from '../../../../../libs/shared/core/src/services/push/ably/ablyController';
import {PushController} from '../../../../../libs/shared/core/src/services/push/common/pushController';
import {
  PushSourcesEnum,
  pushV2Logger,
} from '../../../../../libs/shared/core/src/services/push/common/pushLogger';
import {
  PushPresenceMember,
  PushPresenceMessageTypesEnum,
} from '../../../../../libs/shared/core/src/services/push/common/pushMessage';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {wrapInShallowSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  createChatUserOnlineStatusAction,
  deleteChatUserOnlineStatusAction,
} from '../../../../store/data/chat/chatUserOnlineActions';
import {deleteChatPrecog, updateChatPrecog} from '../../../../store/data/chatPrecogs/chatPrecogsActions';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getChatPushConfig} from '../../../../store/data/dataSelectors';
import {getViewerPanelsChatConversationIds} from '../../../../store/panels/viewer/viewerPanelSelectors';
import {fetchChatAblyToken} from '../../chatPushAuth';
import {ChatPushContext, ChatPushProps} from '../../chatPushContext';

// Max time to wait before disconnecting from the chat instance when no chat conversation is open.
// We do this so as to avoid unnecessary connections when it's not used.
const inactivityGracePeriod = 5 * 60 * 1000;

type ChannelInfoCache = LRUCache<number, ChannelInfo>;
type GetChannelInfoByChatId = (chatId: string) => ChannelInfo | undefined;

interface ChannelInfo {
  conversationId: number;
  frontChatConversationId: string;
  roomName: string;
}

export const ChatPushV2Provider: FC<PropsWithChildren> = ({children}) => {
  const [chatDirectPushMessages, setChatDirectPushMessages] = useState<
    ResourceMap<ReadonlyArray<InboundOptimisticMessage>>
  >({});
  const {pushController, getChannelInfoByChatId} = useChatPushController();
  const contextValue = useChatPushContextValue(pushController, chatDirectPushMessages);
  useChatPushPresence(pushController);
  useChatPrecogs(pushController, getChannelInfoByChatId);
  useChatOptimisticMessage(pushController, getChannelInfoByChatId, setChatDirectPushMessages);
  useChatChannelSubscriptions(pushController);
  return <ChatPushContext.Provider value={contextValue}>{children}</ChatPushContext.Provider>;
};

function useChatChannelSubscriptions(pushController: PushController | undefined) {
  const viewerPanelsChatConversationIds = useAppSelector(
    wrapInShallowSelector(getViewerPanelsChatConversationIds),
  );
  const hasChatChannelsSubject$ = useConstant(
    () => new BehaviorSubject<boolean>(Boolean(viewerPanelsChatConversationIds.length)),
  );

  useEffect(() => {
    hasChatChannelsSubject$.next(Boolean(viewerPanelsChatConversationIds.length));
  }, [hasChatChannelsSubject$, viewerPanelsChatConversationIds]);

  useEffect(() => {
    const subscription = hasChatChannelsSubject$
      .pipe(debounceTime(inactivityGracePeriod))
      .subscribe((hasChannels) => {
        if (hasChannels) {
          return;
        }

        pushController?.stop();
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [hasChatChannelsSubject$, pushController]);

  useEffect(() => {
    if (viewerPanelsChatConversationIds.length) {
      pushController?.start();
    }

    const unsubscribe = pushController?.subscribe(viewerPanelsChatConversationIds.map(String));
    return () => {
      unsubscribe?.();
    };
  }, [pushController, viewerPanelsChatConversationIds]);
}

/**
 * This hook receives optimistic chat messages emitted from the chat via Ably before backend processing.
 * These messages are stored locally in this component.
 * Once processed, the optimistic messages are removed from the UI.
 */
function useChatOptimisticMessage(
  pushController: PushController | undefined,
  getChannelInfoByChatId: GetChannelInfoByChatId,
  setChatDirectPushMessages: Dispatch<SetStateAction<ResourceMap<ReadonlyArray<InboundOptimisticMessage>>>>,
) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const unsubscribe = pushController?.registerEventHandlerDefinition({
      name: 'chat_optimistic_message',
      handler: (event) => {
        const message = enforceType(RInboundOptimisticMessage, event.data);
        const channelInfo = getChannelInfoByChatId(event.data.chatConversationId);

        if (!channelInfo) {
          return noop;
        }

        const {conversationId} = channelInfo;
        return () => {
          setChatDirectPushMessages((current: ResourceMap<ReadonlyArray<InboundOptimisticMessage>>) => {
            const messages = current[conversationId] || [];
            const newMessages = [...messages, message];
            return {
              ...current,
              [conversationId]: newMessages,
            };
          });
        };
      },
    });

    return () => {
      unsubscribe?.();
    };
  }, [dispatch, pushController, getChannelInfoByChatId, setChatDirectPushMessages]);
}

function useChatPrecogs(
  pushController: PushController | undefined,
  getChannelInfoByChatId: GetChannelInfoByChatId,
) {
  const dispatch = useAppDispatch();
  const removePrecogSubject$ = useConstant(() => new Subject<number>());

  useEffect(() => {
    const unsubscribe = pushController?.registerEventHandlerDefinition({
      name: 'chat_precog_updated',
      handler: (event) => {
        const channelInfo = getChannelInfoByChatId(event.data.chat_conversation_id);
        if (!channelInfo) {
          return noop;
        }
        const {conversationId} = channelInfo;
        return () => {
          dispatch(updateChatPrecog(conversationId));
          removePrecogSubject$.next(conversationId);
        };
      },
    });

    return () => {
      unsubscribe?.();
    };
  }, [dispatch, removePrecogSubject$, pushController, getChannelInfoByChatId]);

  useEffect(() => {
    // Max time after which a precog is deleted if no continue message is received.
    const chatPrecogTimeout = 5000;
    const subscription = removePrecogSubject$
      .pipe(
        // Groups observables by conversationId
        groupBy((conversationId) => conversationId),
        // Debounces each conversationId observable independently
        mergeMap((conversationId$) => conversationId$.pipe(debounceTime(chatPrecogTimeout))),
      )
      .subscribe((conversationId) => {
        pushController?.enqueueTask(() => () => {
          dispatch(deleteChatPrecog(conversationId));
        });
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch, removePrecogSubject$, pushController]);
}

function toPresenceMemberIdentifier(member: PushPresenceMember) {
  return `${member.clientId}-${member.connectionId}`;
}

function useChatPushPresence(pushController: PushController | undefined) {
  const members = useConstant<{[conversationId: string]: Set<string>}>(() => ({}));
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!pushController) {
      return noop;
    }

    const unsubscribe = pushController.registerPresenceEventHandler((event) => {
      const conversationId = event.channel;
      members[conversationId] = members[conversationId] || new Set();
      switch (event.type) {
        case PushPresenceMessageTypesEnum.INIT:
          for (const member of event.members) {
            members[conversationId].add(toPresenceMemberIdentifier(member));
          }

          if (event.members.length) {
            dispatch(createChatUserOnlineStatusAction(Number(conversationId)));
          }
          break;
        case PushPresenceMessageTypesEnum.ENTER: {
          // If its their first time entering the room we need to enable the
          // online status.
          const id = toPresenceMemberIdentifier(event.member);
          if (!members[conversationId].has(id)) {
            dispatch(createChatUserOnlineStatusAction(Number(conversationId)));
          }
          members[conversationId].add(id);
          break;
        }
        case PushPresenceMessageTypesEnum.LEAVE: {
          const id = toPresenceMemberIdentifier(event.member);
          if (!members[conversationId].has(id)) {
            pushV2Logger.logUnknownPresenceUser(
              Number(conversationId),
              event.member.clientId,
              event.member.connectionId,
            );
          }

          members[conversationId].delete(id);
          // If there are no more clients, we need to remove the presence indicator.
          if (!members[conversationId].size) {
            delete members[conversationId];
            dispatch(deleteChatUserOnlineStatusAction(Number(conversationId)));
          }
          break;
        }
        default:
        // no-op
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch, members, pushController]);
}

function useChatPushContextValue(
  pushController: PushController | undefined,
  chatDirectPushMessages: ResourceMap<ReadonlyArray<InboundOptimisticMessage>>,
): ChatPushProps {
  return useMemo(
    () => ({
      emitChatPrecog(conversationId, data) {
        pushController?.publish(String(conversationId), 'chat_reply_precog_updated', data);
      },
      emitChatOptimisticMessage(conversationId, data) {
        pushController?.publish(String(conversationId), 'optimistic_message_sent', data);
      },
      chatDirectPushMessages,
    }),
    [chatDirectPushMessages, pushController],
  );
}

function useMemoizedPushConfig() {
  // pushConfig should have a stable reference, but we memoize it just in case
  // to avoid the push controller from being recreated unnecessarily.
  const pushConfig = useAppSelector(getChatPushConfig);
  return useDeepMemo(() => pushConfig, [pushConfig]);
}

function useChatPushController() {
  // Front Chat uses the Ably auth call to fetch both the auth token, and channel info
  // needed to subscribe the channel to Ably. We save this information in this LRU cache
  // for lookup later when the channel is being subscribed.
  const channelInfoCache: ChannelInfoCache = useConstant(() => new LRUCache({max: 20}));
  const pushConfig = useMemoizedPushConfig();
  const company = useAppSelector(getCompany);
  const pushController = useMemo(() => {
    // The Front Chat widget only supports the Ably connector. The widget does not
    // support Socket.IO, so we we do not fallback to Socket.IO in interop mode.
    // PB-39048: Introduce Socket.IO to the Front Chat widget for interop development.
    if (!pushConfig || pushConfig.connector !== PushConnectorsEnum.ABLY) {
      return undefined;
    }

    return new AblyController({
      environment: pushConfig.environment,
      fetchAuthToken: async (channels, cancelToken) => {
        const response = await fetchChatAblyToken(channels.map(Number), cancelToken, company.url);
        if (!response) {
          return '';
        }

        response.rooms.forEach((room) => {
          channelInfoCache.set(room.conversationId, {
            roomName: room.roomName,
            frontChatConversationId: room.frontChatConversationId,
            conversationId: room.conversationId,
          });
        });

        return response.authentication;
      },
      channelNameTransformers: {
        // Ably channel authorization uses the array of conversationIds.
        auth: identity,
        // Ably channel subscription uses the full room name, which is included
        // in the ably auth response.
        subscribe: (conversationId) => {
          const info = channelInfoCache.get(Number(conversationId));
          if (!info) {
            throw new Error(`Missing channel info for conversation id ${conversationId}`);
          }
          return buildAblyChannelName(info.roomName, pushConfig.roomsPrefix);
        },
      },
      shouldEnterPresence: () => false,
      logSource: PushSourcesEnum.CHAT,
      shouldRefreshBootOnReconnect: false,
    });
  }, [channelInfoCache, company.url, pushConfig]);

  const getChannelInfoByChatId: GetChannelInfoByChatId = useCallback(
    (chatConversationId) =>
      channelInfoCache.find((info) => info.frontChatConversationId === chatConversationId),
    [channelInfoCache],
  );

  return {
    pushController,
    getChannelInfoByChatId,
  };
}
